"use client";

import React, { useMemo } from "react";
import type { VariantType } from "@shared/graphql.types";
import { useLocale } from "hooks";
import { defaultSchema } from "components/Seo/DefaultSchema";

type Props = {
  variant: VariantType;
  urlRef: string | null;
};

export const PDPSeo: React.FC<Props> = ({ variant, urlRef }) => {
  const locale = useLocale();
  const {
    sku,
    brandName,
    title,
    pricing,
    review,
    longDescription,
    metaData,
    origin,
    detailsMedia,
    media
  } = variant;

  const isGoogleShopping = urlRef === "pla_g";
  const seoImage = detailsMedia ?? media?.[0];

  const reviews = useMemo(
    () =>
      review?.reviews?.map((rev) => {
        return {
          "@type": "Review",
          author: rev?.authorName,
          datePublished: rev?.date,
          reviewBody: rev?.comment,
          name: rev?.title,
          reviewRating: {
            "@type": "Rating",
            bestRating: rev?.comment,
            ratingValue: rev?.stars,
            worstRating: review?.worstRating
          }
        };
      }),
    [review]
  );

  const pageDescription = metaData?.description ?? longDescription ?? "";

  const currency = locale === "en-US" ? "USD" : "GBP";

  const typicalPriceOffers = {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceCurrency: currency,
    price: pricing.guest,
    shippingDetails: {
      "@id":
        "https://beautypiehelp.zendesk.com/hc/en-gb/articles/5986781063442-DELIVERY-OPTIONS-AND-CHARGES"
    }
  };

  const googleShoppingOffer = {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceCurrency: currency,
    price: pricing.members,
    shippingDetails: {
      "@id":
        "https://beautypiehelp.zendesk.com/hc/en-gb/articles/5986781063442-DELIVERY-OPTIONS-AND-CHARGES"
    }
  };

  const data = [
    defaultSchema(locale),
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: title,
      brand: brandName ?? "BEAUTY PIE",
      description: pageDescription,
      countryOfOrigin: origin?.name ?? origin?.code ?? "",
      image: seoImage?.thumbnails?.size_OTF_BETA ?? "",
      sku,
      offers: {
        ...(!isGoogleShopping
          ? { ...typicalPriceOffers }
          : { ...googleShoppingOffer })
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: review?.stars ? Number(review.stars.toFixed(1)) : 0,
        reviewCount: review?.count ?? 0
      },
      review: reviews
    }
  ];

  return (
    <>
      <script
        id="pdp-seo-tag"
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      />
    </>
  );
};
